import { useContext, useLayoutEffect, useMemo } from 'react';

import { CookieContext } from '@imgly/imgly-website-components';
import { debounce } from 'lodash';

export const usePersistentScrollPosition = (
  ref: React.MutableRefObject<HTMLAreaElement | null>,
): void => {
  const { trackFunctional } = useContext(CookieContext);

  // add the scrollTop position to the sessionStorage
  const handleScroll = () => {
    if (ref.current) {
      if (trackFunctional) {
        sessionStorage.setItem(
          'navigation-scroll-pos',
          ref.current.scrollTop.toString(),
        );
      }
    }
  };

  // debounce the function to avoid performance issues
  const debouncedHandleScroll = useMemo(() => debounce(handleScroll, 100), []);

  useLayoutEffect(() => {
    // get the previous position from the session storage
    const scrollPos = trackFunctional
      ? parseInt(sessionStorage.getItem('navigation-scroll-pos') || '0', 10)
      : 0;

    // only update the value if there is a difference
    if (ref.current && ref.current?.scrollTop !== scrollPos) {
      ref.current?.scrollTo(0, scrollPos);
    }

    // add the event listener
    if (ref.current) {
      ref.current.addEventListener('scroll', debouncedHandleScroll);
    }

    // add a event listener which will be called before the page is unloaded to remove the item
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('navigation-scroll-pos');
    });

    return () => {
      if (ref.current) {
        ref.current.removeEventListener('scroll', handleScroll);
        debouncedHandleScroll.cancel();
      }
    };
  }, []);
};
